import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationContactDocument,
  UpdateOrganizationContactDocument,
  DeleteOrganizationContactDocument,
  type GetOrganizationContactQuery,
  type UpdateOrganizationContactMutation,
  type UpdateOrganizationContactMutationVariables,
  type DeleteOrganizationContactMutation,
  type DeleteOrganizationContactMutationVariables,
} from '@/generated/root-types';
import { setOverrideTenantId } from '@gastrosoft/shared/utils';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import {
  detailBackButtonSx,
  detailFieldGridSx,
  detailFieldSx,
  detailFullRowSx,
  detailHeaderRowSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

export default function OrganizationContactDetail() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [salutation, setSalutation] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetOrganizationContactQuery>(
    GetOrganizationContactDocument,
    {
      variables: { id: id! },
      skip: !id,
    },
  );

  const c = data?.organizationContact;

  useEffect(() => {
    if (c?.tenantId) setOverrideTenantId(c.tenantId);
    else setOverrideTenantId(null);
    return () => setOverrideTenantId(null);
  }, [c?.tenantId]);

  useEffect(() => {
    if (!c) return;
    setFirstName(c.firstName ?? '');
    setLastName(c.lastName ?? '');
    setEmail(c.email ?? '');
    setPosition(c.position ?? '');
    setSalutation(c.salutation ?? '');
  }, [c]);

  const [updateC, { loading: saving }] = useMutation<
    UpdateOrganizationContactMutation,
    UpdateOrganizationContactMutationVariables
  >(UpdateOrganizationContactDocument);

  const [deleteC, { loading: deleting }] = useMutation<
    DeleteOrganizationContactMutation,
    DeleteOrganizationContactMutationVariables
  >(DeleteOrganizationContactDocument);

  const handleSave = async () => {
    if (!id) return;
    try {
      const res = await updateC({
        variables: {
          input: {
            id,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            email: email || undefined,
            position: position || undefined,
            salutation: salutation || undefined,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.updateOrganizationContact);
      if (pe) {
        showError(pe);
        return;
      }
      showSuccess(t('common.saved'));
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const res = await deleteC({ variables: { input: { id } } });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.deleteOrganizationContact);
      if (pe || !res.data?.deleteOrganizationContact?.boolean) {
        showError(pe || t('errors.generic'));
        return;
      }
      showSuccess(t('common.deleted'));
      navigate('/organization-contacts');
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...detailPageContainerSx, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !c) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/organization-contacts')}>
          {t('common.back')}
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>
          {error?.message ?? t('errors.generic')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={detailHeaderRowSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/organization-contacts')}
          sx={detailBackButtonSx}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {t('orgContacts.title')}
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Box sx={detailFieldGridSx}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ...detailFullRowSx, fontWeight: 600 }}
          >
            {c.organization?.name}
          </Typography>
          <TextField
            sx={detailFieldSx}
            label={t('common.id')}
            value={c.id}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.organizationId')}
            value={c.organizationId}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.tenantId')}
            value={c.tenantId}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.tenant')}
            value={c.tenant?.name ?? '–'}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.created')}
            value={formatDateTime(c.createdAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.updated')}
            value={formatDateTime(c.updatedAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgContacts.salutation')}
            value={salutation}
            onChange={(e) => setSalutation(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgContacts.firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgContacts.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('organizationDetail.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <TextField
            sx={detailFieldSx}
            label={t('orgContacts.position')}
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            fullWidth
          />
          <Box sx={{ ...detailFullRowSx, display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {t('common.save')}
            </Button>
            {superAdmin && (
              <Button color="error" onClick={() => setDeleteOpen(true)}>
                {t('common.delete')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('orgContacts.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('orgContacts.confirmDeleteBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
